import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Clock, CheckCircle, XCircle, CalendarDays } from "lucide-react";
import { useMyAttendance } from "../../hooks";

const MyAttendance = () => {
  // Use custom hook for data fetching with month filtering
  const { 
    attendance, 
    loading, 
    stats, 
    selectedMonth, 
    setSelectedMonth, 
    monthOptions 
  } = useMyAttendance();

  return (
    <div className="space-y-6" data-testid="my-attendance-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">My Attendance</h1>
          <p className="text-slate-500">View your attendance history</p>
        </div>
        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
          <SelectTrigger className="w-[200px] input-field" data-testid="month-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {monthOptions.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Stats - using hook's stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="stat-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-green-100"><CheckCircle className="h-6 w-6 text-green-700" /></div>
              <div><p className="text-2xl font-bold text-slate-800">{stats.present}</p><p className="text-sm text-slate-500">Present</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-red-100"><XCircle className="h-6 w-6 text-red-700" /></div>
              <div><p className="text-2xl font-bold text-slate-800">{stats.absent}</p><p className="text-sm text-slate-500">Absent</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-100"><Clock className="h-6 w-6 text-amber-700" /></div>
              <div><p className="text-2xl font-bold text-slate-800">{stats.late}</p><p className="text-sm text-slate-500">Late</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="card-soft" data-testid="attendance-table">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>
          ) : attendance.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No attendance records for this month.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">Date</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">Check In</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">Check Out</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {attendance.map(r => (
                  <TableRow key={r.id} className="hover:bg-slate-50/50" data-testid={`att-row-${r.id}`}>
                    <TableCell className="font-medium text-slate-800">
                      <div className="flex items-center gap-1.5">
                        <CalendarDays className="h-4 w-4 text-slate-400" />
                        {r.date}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">{r.check_in || "—"}</TableCell>
                    <TableCell className="text-slate-600">{r.check_out || "—"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        r.status === "present" ? "bg-green-50 text-green-700 border-green-200" :
                        r.status === "late" ? "bg-amber-50 text-amber-700 border-amber-200" :
                        "bg-red-50 text-red-700 border-red-200"
                      }>{r.status}</Badge>
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">{r.notes || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyAttendance;
